import React, { useCallback, useRef, useEffect, useState } from 'react';
import { Minimize2, Trash2, Sparkles, Info } from 'lucide-react';
import { useAskSidebar, type ChatMessage, type AgentToolCall } from '../../contexts/AskSidebarContext';
import { agentApi } from '../../services/reportApi';
import { ChatThread } from '../ask/ChatThread';
import { ChatInput } from '../ask/ChatInput';
import { AgentTabBar } from './AgentTabBar';

const CAPABILITIES = [
  'Search and cross-reference case documents',
  'Review chronologies and timelines',
  'Consult clinical guidelines (NICE, BNF)',
  'Search UK case law and limitation periods',
  'Check drug interactions',
  'Query PubMed medical literature',
  'Generate and refine reports',
];

const CapabilitiesBadge: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button className="flex items-center gap-1 text-[11px] text-medical-400 dark:text-medical-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
        <Info className="h-3 w-3" />
        <span>Capabilities</span>
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 w-64 bg-white dark:bg-medical-800 border border-medical-200 dark:border-medical-600 rounded-lg shadow-lg p-3 z-50 animate-fade-in">
          <p className="text-[10px] font-semibold text-medical-600 dark:text-medical-300 uppercase tracking-wide mb-2">
            What the assistant can do
          </p>
          <ul className="space-y-1.5">
            {CAPABILITIES.map((cap) => (
              <li key={cap} className="flex items-start gap-1.5 text-[11px] text-medical-500 dark:text-medical-400 leading-snug">
                <span className="text-primary-500 mt-px">·</span>
                {cap}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

interface Props {
  caseId: string;
}

export const AgentFullView: React.FC<Props> = ({ caseId }) => {
  const {
    messages, isLoading,
    toggleMaximize, conversationId,
    activeTabId, updateTab, createTab, closeTab,
  } = useAskSidebar();

  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll on new content
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Escape key to minimize
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') toggleMaximize();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [toggleMaximize]);

  const handleSendMessage = useCallback(async (content: string) => {
    // Capture the tab ID at send time so streaming routes to the correct tab
    const sendingTabId = activeTabId;
    if (!sendingTabId) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date(),
    };

    // Update title if this is the first message in a new tab
    const patch: Partial<import('../../contexts/AskSidebarContext').TabState> = {
      messages: [...messages, userMessage],
      isLoading: true,
    };
    if (messages.length === 0) {
      patch.title = content.length > 50 ? content.substring(0, 47) + '...' : content;
    }
    updateTab(sendingTabId, patch);

    // Agentic tool-use loop
    const assistantMsgId = (Date.now() + 1).toString();
    const assistantMessage: ChatMessage = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
      isThinking: true,
      toolCalls: [],
    };
    updateTab(sendingTabId, {
      messages: [...messages, userMessage, assistantMessage],
      isStreaming: true,
    });

    try {
      let accumulated = '';
      const currentToolCalls: AgentToolCall[] = [];

      // Track messages locally and push to tab state via updateTab
      let localMessages = [...messages, userMessage, assistantMessage];

      const patchLastAssistant = (patch: Partial<ChatMessage>) => {
        const last = localMessages[localMessages.length - 1];
        if (last?.id === assistantMsgId) {
          localMessages = [...localMessages.slice(0, -1), { ...last, ...patch }];
          updateTab(sendingTabId, { messages: [...localMessages] });
        }
      };

      await agentApi.chat(
        { message: content, caseId, conversationId },
        {
          onThinking: () => {
            patchLastAssistant({ isThinking: true });
          },
          onToolCall: (event) => {
            currentToolCalls.push({
              toolName: event.toolName,
              toolUseId: event.toolUseId,
              input: event.input,
              status: 'running',
            });
            patchLastAssistant({ isThinking: false, toolCalls: [...currentToolCalls] });
          },
          onToolResult: (event) => {
            const tc = currentToolCalls.find(t => t.toolUseId === event.toolUseId);
            if (tc) {
              tc.status = 'done';
              tc.summary = event.summary;
              if (event.widgetData) {
                tc.widgetData = event.widgetData;
              }
            }
            patchLastAssistant({ toolCalls: [...currentToolCalls] });
          },
          onToken: (token) => {
            accumulated += token;
            patchLastAssistant({ content: accumulated, isThinking: false });
          },
          onSources: (sources) => {
            patchLastAssistant({
              sources: sources.map(s => ({
                documentId: s.documentId || '',
                documentName: s.documentName || 'Unknown',
                pageNumber: s.pageNumber,
                excerpt: s.excerpt,
                sourceText: s.sourceText,
                chunkIndex: s.chunkIndex,
              })),
            });
          },
          onDone: (event) => {
            if (event.conversationId) {
              updateTab(sendingTabId, { conversationId: event.conversationId });
            }
            patchLastAssistant({ isStreaming: false, isThinking: false });
            updateTab(sendingTabId, { isStreaming: false });
          },
          onError: (err) => {
            console.error('[Agent] Stream error:', err);
            patchLastAssistant({
              isStreaming: false,
              isThinking: false,
              content: accumulated || 'Sorry, I encountered an error. Please try again.',
            });
            updateTab(sendingTabId, { isStreaming: false });
          },
        },
      );
    } catch (err) {
      console.error('[Agent] Error:', err);
      updateTab(sendingTabId, {
        messages: [...messages, userMessage, {
          id: (Date.now() + 2).toString(),
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date(),
        }],
        isLoading: false,
        isStreaming: false,
      });
    } finally {
      updateTab(sendingTabId, { isLoading: false });
    }
  }, [caseId, conversationId, messages, activeTabId, updateTab]);

  const handleClearTab = useCallback(() => {
    if (activeTabId) {
      closeTab(activeTabId);
      createTab();
    }
  }, [activeTabId, closeTab, createTab]);

  const welcomeMessage: ChatMessage = {
    id: 'welcome',
    role: 'assistant',
    content: "I'm your AI case assistant. I can search documents, read specific files, view the chronology, check analyses, and answer questions about this case. What would you like to know?",
    timestamp: new Date(),
  };

  const displayMessages = messages.length === 0 ? [welcomeMessage] : messages;
  const showSuggestions = messages.length === 0;

  return (
    <div className="flex flex-col h-full bg-medical-50 dark:bg-medical-900">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 bg-white dark:bg-medical-800 border-b border-medical-200 dark:border-medical-700">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 rounded-lg bg-primary-600 dark:bg-primary-700 flex items-center justify-center shadow-sm">
            <Sparkles className="h-3.5 w-3.5 text-white" />
          </div>
          <span className="text-sm font-semibold text-medical-800 dark:text-medical-200">ClinLexis Assistant</span>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button
              onClick={handleClearTab}
              className="p-1.5 text-medical-400 hover:text-red-500 dark:hover:text-red-400 rounded"
              title="Clear conversation"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={toggleMaximize}
            className="p-1.5 text-medical-400 hover:text-medical-600 dark:hover:text-medical-300 rounded"
            title="Minimize (Esc)"
          >
            <Minimize2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <AgentTabBar caseId={caseId} />

      {/* Chat area — centered with max-width */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <ChatThread messages={displayMessages} isLoading={isLoading} variant="full" />
        </div>
      </div>

      {/* Input — sticky bottom */}
      <div className="border-t border-medical-200 dark:border-medical-700 bg-white dark:bg-medical-800">
        <div className="max-w-5xl mx-auto px-6 py-3 space-y-2">
          <div className="flex items-center gap-1.5">
            <CapabilitiesBadge />
          </div>
          <ChatInput
            onSend={handleSendMessage}
            isLoading={isLoading}
            placeholder="Ask anything — the AI will search, read, and analyse..."
            suggestedQuestions={[
              'What documents are in this case?',
              'Summarise the key events in the chronology',
              'Are there gaps in the medical records?',
              'What are the most significant clinical findings?',
            ]}
            showSuggestions={showSuggestions}
          />
        </div>
      </div>
    </div>
  );
};
